import { useState, useMemo } from 'react';
import { Typography, Box, Button, Grid, Paper, Snackbar, Alert, TextField } from '@mui/material';
import { Breadcrumbs } from '../../components/shared';
import BREADCRUMB_NAMES from '../../constants/breadcrumbNames';
import { routeConstants } from '../../constants/routeConstants';
import { useNavigate } from 'react-router-dom';
import { useCreateManageAreaTaxLocationMutation } from '../../services/ManageAreaTaxLocations/ManageAreaTaxLocationsService';

const AssignAreaTaxLocation = () => {
  const navigate = useNavigate();
  const [createAreaTaxLocation, { isLoading }] = useCreateManageAreaTaxLocationMutation();

  const [formData, setFormData] = useState({
    location: '',
  });
  const [fieldTouched, setFieldTouched] = useState({
    location: false,
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    SETTINGS: { MANAGE_AREA_TAX_LOCATION, SETTINGS },
  } = BREADCRUMB_NAMES;

  const breadcrumbData = useMemo(
    () => [
      { text: SETTINGS, redirection: routeConstants.SETTING_ROUTE },
      {
        text: MANAGE_AREA_TAX_LOCATION,
        redirection: `${routeConstants.SETTING_ROUTE}/${routeConstants.MANAGE_AREA_TAX_LOCATION}`,
      },
      { text: 'Add' },
    ],
    [],
  );

  const disableSave = !formData.location.trim() || isLoading;

  const shouldShowError = (fieldName) => fieldTouched[fieldName] || formSubmitted;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleLocationChange = (event) => {
    setFieldTouched((prev) => ({ ...prev, location: true }));
    setFormData((prev) => ({ ...prev, location: event.target.value }));
  };

  const handleSave = async () => {
    setFormSubmitted(true);
    setFieldTouched({ location: true });

    if (!formData.location.trim()) {
      showSnackbar('Please enter a location', 'error');
      return;
    }

    createAreaTaxLocation({
      areaTaxLocationName: formData.location.trim(),
    })
      .unwrap()
      .then(() => {
        navigate(`/${routeConstants.SETTING_ROUTE}/${routeConstants.MANAGE_AREA_TAX_LOCATION}`);
      })
      .catch((error) => {
        console.error('Failed to create area tax location:', error);
        const errorMessage = error.data?.message || 'Failed to create area tax location. Please try again.';
        showSnackbar(errorMessage, 'error');
      });
  };

  return (
    <div>
      <Box mb={2}>
        <Breadcrumbs variant="body2" underline="hover" data={breadcrumbData} />
      </Box>
      <Typography variant="h1" mb={3}>
        Add Area Tax Location
      </Typography>

      <Box component={Paper} p={3}>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Area Tax Location Name"
              variant="outlined"
              value={formData.location}
              onChange={handleLocationChange}
              error={shouldShowError('location') && !formData.location.trim()}
              helperText={shouldShowError('location') && !formData.location.trim() ? 'Location name is required' : ''}
              onBlur={() => setFieldTouched((prev) => ({ ...prev, location: true }))}
              placeholder="Enter location name (e.g., Crook, WY)"
            />
          </Grid>
        </Grid>

        <Box display="flex" justifyContent="flex-start" gap={2} mt={3}>
          <Button variant="contained" color="primary" disabled={disableSave} onClick={handleSave}>
            {isLoading ? 'Adding...' : 'Add'}
          </Button>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AssignAreaTaxLocation;
